import { Icon } from '@zoom/components';
import classnames from 'classnames';
import { addDays, addSeconds, format, isValid, parse } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldMetaState } from 'react-final-form';
import PerfectScrollbar from 'react-perfect-scrollbar';
import TimePickerExternal from 'react-time-picker';
import { Dropdown, DropdownMenu, DropdownToggle, Label } from 'reactstrap';

import { TimePickerOption } from './TimePickerOption';
import { useAuth } from '../../app/AuthorizationProvider';
import { MIN_15 } from '../../scenes/Scheduling/config';
import {
  ONE_HOUR_IN_SECONDS,
  TIME_FORMAT_12_HOURS,
  TIME_FORMAT_24_HOURS,
  TimeFormat,
  convertTimeFrom24to12HoursFormat,
  convertTimeFromNumberToStringValue,
  convertTimeFromStringToNumberValue,
} from '../../utils/time';
import { TValidator } from '../../utils/validations/types';

import type { LooseValue } from 'react-time-picker/dist/cjs/shared/types';

const HEIGHT_OF_DROPDOWN_ITEM = 29;

type TimePickerProps = {
  alignMenuToStart?: boolean;
  className?: string;
  'data-test-id'?: string;
  disabled?: boolean;
  inputName: string;
  label: string | React.ReactNode;
  max?: number;
  meta: FieldMetaState<'input'>;
  min?: number;
  onBlur?: (event?: any) => void;
  onFocus?: (event?: any) => void;
  onSelect: (value: string) => void;
  onToggle?: (isOpen?: boolean) => void;
  roundBy?: number;
  timeFormat: TimeFormat;
  value?: string;
};

export const TimePicker: React.FC<TimePickerProps> = ({
  alignMenuToStart,
  className,
  'data-test-id': dataTestId,
  disabled,
  inputName,
  label,
  max = ONE_HOUR_IN_SECONDS * 24, // 24:00 in seconds
  meta: { active, dirty, modified, valid },
  min = 0, // 00:00 in seconds
  onBlur,
  onFocus,
  onSelect,
  onToggle = () => null,
  roundBy = MIN_15 / 1000, // value in seconds
  timeFormat,
  value,
}) => {
  const { localeObj } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setFocused] = useState(false);
  const [tempValue, setTempValue] = useState<string | undefined>(undefined);
  const [containInvalidTempValue, setContainInvalidTempValue] = useState(false);

  const dropdownContentRef = useRef<HTMLDivElement>(null);
  const [optionsContainerRef, setOptionsContainerRef] = useState<HTMLElement | null>(null);

  const isValueEmpty = value === undefined || (typeof value === 'string' && value === '');
  const isAmPmFormat = timeFormat === TIME_FORMAT_12_HOURS;
  const formattedValue = isAmPmFormat && !isValueEmpty ? convertTimeFrom24to12HoursFormat(value, localeObj) : value;

  const minAsString = convertTimeFromNumberToStringValue(min, localeObj);
  const maxAsString = convertTimeFromNumberToStringValue(max, localeObj);

  const handleToggle = () => {
    if (isOpen) {
      setTempValue(undefined);
      onBlur?.();
      setFocused(false);
      setContainInvalidTempValue(false);
    }
    setIsOpen(!isOpen);
    onToggle(!isOpen);
  };

  const options = useMemo(() => {
    const minRoundedValue = min % roundBy ? min + (roundBy - (min % roundBy)) : min;
    const maxRoundedValue = max % roundBy ? max - (roundBy - (max % roundBy)) : max;

    const minDate = parse(
      convertTimeFromNumberToStringValue(minRoundedValue, localeObj),
      TIME_FORMAT_24_HOURS,
      new Date()
    );
    let maxDate = parse(
      convertTimeFromNumberToStringValue(maxRoundedValue, localeObj),
      TIME_FORMAT_24_HOURS,
      new Date()
    );

    if (minDate.getTime() === maxDate.getTime()) {
      maxDate = addDays(maxDate, 1);
    }

    let currentDate = minDate;
    const optionsValues: Array<{ label: string; value: string }> = [];

    while (currentDate < maxDate) {
      optionsValues.push({
        label: format(currentDate, timeFormat, { locale: localeObj }),
        value: format(currentDate, TIME_FORMAT_24_HOURS, { locale: localeObj }),
      });

      currentDate = addSeconds(currentDate, roundBy);
    }

    return optionsValues;
  }, [min, max, roundBy, timeFormat, localeObj]);

  useEffect(() => {
    if (isOpen && value && optionsContainerRef) {
      const selectedItemIndex = options.findIndex((option) => option.value === value) ?? 0;

      // scroll to selected time which should be placed in the center of container
      optionsContainerRef.scrollTop = (selectedItemIndex - 3) * HEIGHT_OF_DROPDOWN_ITEM || 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, optionsContainerRef, value]);

  const selectOptionHandler = (optionValue: string) => {
    onSelect(optionValue);
    setIsOpen(false);
    setFocused(false);
    setTempValue(undefined);
    setContainInvalidTempValue(false);
  };

  const renderOptionItems = ({
    isActive,
    label: optionLabel,
    value: optionValue,
    tabIndex,
  }: {
    isActive: boolean;
    label: string;
    value: string;
    tabIndex?: number;
  }) => (
    <TimePickerOption
      active={isActive}
      key={optionValue}
      onClick={() => selectOptionHandler(optionValue)}
      tabIndex={tabIndex}
      value={optionValue}
    >
      {optionLabel}
    </TimePickerOption>
  );

  const renderOptions = options.map((option, index) => {
    const isActive = option.value === value;
    const tabIndex = index === 0 ? 0 : -1;
    const selectedTabIndex = isActive ? 0 : -1;

    return renderOptionItems({
      ...option,
      isActive,
      tabIndex: isValueEmpty ? tabIndex : selectedTabIndex,
    });
  });

  let labelContent = '';

  if (!containInvalidTempValue) {
    labelContent = tempValue ?? formattedValue ?? '';
  }

  const handleTimeChange = (newValue: LooseValue | React.ChangeEvent<HTMLInputElement>) => {
    let newTime;

    if (
      newValue === null ||
      (typeof newValue === 'object' && !(newValue as React.ChangeEvent<HTMLInputElement>)?.target?.value)
    ) {
      /**
       * TimePicker invokes this handlers twice in a row, first time with event object,
       * second time with value only if it was able to parse some based on input
       */
      setContainInvalidTempValue(true);
      return;
    }

    if (containInvalidTempValue) {
      setContainInvalidTempValue(false);
    }

    if (typeof newValue === 'string') {
      // for some reason, updated value is always formatted in 24 hours format...
      const parsedDate = parse(newValue, TIME_FORMAT_24_HOURS, new Date());
      if (!isValid(parsedDate)) {
        return;
      }

      newTime = format(parsedDate, timeFormat, { locale: localeObj });
    } else if (isValid(newValue)) {
      newTime = format(newValue as Date, timeFormat, { locale: localeObj });
    }

    if (newTime) {
      setTempValue(newTime);
    }
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLElement>) => {
    setFocused(false);
    onBlur?.(e);
    onToggle(false);
  };

  const onFocusHandler = (e: React.FocusEvent<HTMLElement>) => {
    setFocused(true);
    onFocus?.(e);
    onToggle(true);
    setIsOpen(true);
  };

  const onToggleClickHandler = () => {
    dropdownContentRef?.current?.querySelectorAll('input')?.[1]?.focus();
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (containInvalidTempValue) {
        onSelect('');
        setTempValue(undefined);
      } else if (tempValue && tempValue !== value) {
        let tempValueAsNumber = convertTimeFromStringToNumberValue(tempValue);
        const tempValueModulo = tempValueAsNumber % roundBy;
        if (tempValueModulo) {
          // increase value to nearest roundBy
          tempValueAsNumber += roundBy - tempValueModulo;
        }

        onSelect(convertTimeFromNumberToStringValue(tempValueAsNumber));
        setTempValue(undefined);
      }
      setIsOpen(!isOpen);
    }
    if (e.key === 'Escape') {
      setTempValue(undefined);
      if (isOpen) {
        setIsOpen(false);
      }
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      setContainInvalidTempValue(true);
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (!isOpen) {
        setIsOpen(true);
      }

      if ((e.target as HTMLInputElement)?.name === 'amPm') {
        // let user open select menu for choosing am/pm value
        e.stopPropagation();
        return;
      }

      e.preventDefault();

      if (e.key === 'ArrowUp') {
        if ((e.target as HTMLInputElement)?.name === 'minute') {
          // increase by roundBy value
          const newValue = convertTimeFromStringToNumberValue((tempValue ?? value) || minAsString) + roundBy;

          if (newValue < max) {
            handleTimeChange(convertTimeFromNumberToStringValue(newValue, localeObj));
          }
        } else {
          const increment = Math.max(roundBy, ONE_HOUR_IN_SECONDS);
          const newValue = convertTimeFromStringToNumberValue((tempValue ?? value) || minAsString) + increment;

          if (newValue < max) {
            handleTimeChange(convertTimeFromNumberToStringValue(newValue, localeObj));
          }
        }
      } else if ((e.target as HTMLInputElement)?.name === 'minute') {
        // decrease by roundBy value
        const newValue = convertTimeFromStringToNumberValue((tempValue ?? value) || maxAsString) - roundBy;

        if (newValue >= min) {
          handleTimeChange(convertTimeFromNumberToStringValue(newValue, localeObj));
        }
      } else {
        const increment = Math.max(roundBy, ONE_HOUR_IN_SECONDS);
        const newValue = convertTimeFromStringToNumberValue((tempValue ?? value) || maxAsString) - increment;
        if (newValue >= min) {
          handleTimeChange(convertTimeFromNumberToStringValue(newValue, localeObj));
        }
      }
    }
  };

  return (
    <Dropdown
      className={classnames('time-input', className)}
      data-test-id={dataTestId}
      isOpen={isOpen}
      onBlur={onBlurHandler}
      onFocus={onFocusHandler}
      toggle={handleToggle}
    >
      <div ref={dropdownContentRef}>
        <DropdownToggle
          caret
          className={classnames('time-input__toggler', 'btn', 'btn-info', {
            'time-input__toggler--active': active || isOpen || isFocused,
            'time-input__toggler--valid': valid && (dirty || modified || value),
            'time-input__toggler--invalid': !valid && (dirty || modified),
          })}
          color="info"
          disabled={disabled ?? false}
          tag="div"
          onClick={onToggleClickHandler}
        >
          <div className="time-input__input-wrapper">
            <Label
              className={classnames('TextInputFloating', 'Label', {
                active: isOpen || labelContent || isFocused,
              })}
            >
              {label}
            </Label>
            <TimePickerExternal
              clearIcon={null}
              className={classnames('react-time-picker__container', {
                'react-time-picker__container--hidden': !labelContent && !isOpen && !isFocused,
              })}
              disableClock
              format={timeFormat}
              name={inputName}
              onChange={handleTimeChange}
              value={labelContent ? parse(labelContent, timeFormat, new Date()) : ''}
              {...{
                // component will accept following props even though they are not listed in prop types
                onClick: (e: Event) => {
                  if (!isOpen) {
                    handleToggle();
                  }
                  e.stopPropagation();
                },
                onKeyDown: onKeyDownHandler,
              }}
            />
          </div>
          <Icon className="ml-2" kind="chevron-down" width={12} height={7} color="white" />
        </DropdownToggle>
        <DropdownMenu
          className="time-input__menu-wrapper"
          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
          positionFixed
          right={alignMenuToStart}
        >
          <div className="time-input__menu-section-container py-2">
            <PerfectScrollbar
              className="dropdown-menu-scroll w-100"
              containerRef={setOptionsContainerRef}
              options={{
                minScrollbarLength: 40,
                wheelPropagation: false,
              }}
            >
              {renderOptions}
            </PerfectScrollbar>
          </div>
        </DropdownMenu>
      </div>
    </Dropdown>
  );
};

export interface SelectFieldProps {
  'data-test-id'?: string;
  alignMenuToStart?: boolean;
  className?: string;
  disabled?: boolean;
  label: string;
  max?: number;
  meta?: FieldMetaState<'input'>;
  min?: number;
  name: string;
  roundBy?: number;
  timeFormat: TimeFormat;
  validate?: TValidator;
}

export const TimePickerField: React.FC<SelectFieldProps> = ({ className, name, validate, meta, ...restProps }) => (
  <Field name={name} validate={validate}>
    {({ input: { value, onChange, meta: inputMeta } }) => (
      <TimePicker
        {...restProps}
        className={classnames(className, 'time-duration-select')}
        inputName={name}
        onSelect={(newValue: string) => onChange(newValue)}
        value={value}
        meta={meta || inputMeta}
      />
    )}
  </Field>
);
